import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PetPlace {
  id: string;
  name: string;
  category: string | null;
  address: string | null;
  pet_policy_summary: string | null;
  is_verified: boolean | null;
  is_premium: boolean | null;
  owner_id: string | null;
  photo_url: string | null;
  claim_contact: any;
  hours: any;
  latitude: number;
  longitude: number;
  distance_m: number;
}

export interface NearbyDedupPlace {
  id: string;
  name: string;
  category: string | null;
  distance_m: number;
}

export const PLACE_CATEGORIES = [
  'dog_park',
  'cat_cafe',
  'dog_cafe',
  'pet_shop',
  'restaurant',
] as const;
export type PlaceCategory = (typeof PLACE_CATEGORIES)[number];

interface UsePetPlacesParams {
  lat: number | null;
  lng: number | null;
  radiusKm: number;
  category: PlaceCategory | null;
  enabled?: boolean;
}

export function usePetPlaces({ lat, lng, radiusKm, category, enabled = true }: UsePetPlacesParams) {
  return useQuery({
    queryKey: ['pet-places', lat?.toFixed(3), lng?.toFixed(3), radiusKm, category],
    enabled: enabled && lat !== null && lng !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<PetPlace[]> => {
      const { data, error } = await supabase.rpc('get_places_in_radius', {
        user_lat: lat!,
        user_lng: lng!,
        radius_km: radiusKm,
        category_filter: category,
      });
      if (error) throw error;
      return (data as PetPlace[]) ?? [];
    },
  });
}

export function useNearbyPlacesForDedup(lat: number | null, lng: number | null, radiusM = 50) {
  return useQuery({
    queryKey: ['pet-places-dedup', lat?.toFixed(5), lng?.toFixed(5), radiusM],
    enabled: lat !== null && lng !== null,
    staleTime: 30_000,
    queryFn: async (): Promise<NearbyDedupPlace[]> => {
      const { data, error } = await supabase.rpc('get_nearby_places_for_dedup', {
        user_lat: lat!,
        user_lng: lng!,
        radius_m: radiusM,
      });
      if (error) throw error;
      return (data as NearbyDedupPlace[]) ?? [];
    },
  });
}

export interface AddPlaceInput {
  name: string;
  category: PlaceCategory;
  pet_policy_summary?: string;
  address?: string;
  latitude: number;
  longitude: number;
}

export function useAddPlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddPlaceInput) => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) throw new Error('Not authenticated');

      const wkt = `SRID=4326;POINT(${input.longitude} ${input.latitude})`;
      const { data, error } = await supabase
        .from('pet_places')
        .insert({
          name: input.name,
          category: input.category,
          pet_policy_summary: input.pet_policy_summary || null,
          address: input.address || null,
          location_geom: wkt,
          added_by: uid,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pet-places'] });
    },
  });
}

export function useClaimPlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { placeId: string; contact: Record<string, string> }) => {
      const { data, error } = await supabase.rpc('claim_pet_place', {
        p_place_id: vars.placeId,
        p_contact: vars.contact,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pet-places'] });
    },
  });
}

export function useDisputeClaim() {
  return useMutation({
    mutationFn: async (vars: { placeId: string; reason: string; contact?: Record<string, string> }) => {
      const { data, error } = await supabase.rpc('dispute_pet_place_claim', {
        p_place_id: vars.placeId,
        p_reason: vars.reason,
        p_contact: vars.contact ?? null,
      });
      if (error) throw error;
      return data;
    },
  });
}
